import React, {
    useRef,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from "react";
import Editor from "@monaco-editor/react";

const CodeAnswerEditor = (
    {
        value,
        onChange,
        language = "javascript",
        height = "300px",
        onKeyDown,
        onPaste,
        ...props
    },
    forwardedRef
) => {
    // console.log("CodeAnswerEditor render ✅");
    const editorRef = useRef(null);
    const containerRef = useRef(null);
    const monacoContainerRef = useRef(null);

    // Expose a simple API to parent via ref (for focusing from your hook)
    useImperativeHandle(forwardedRef, () => ({
        focus: () => {
            if (editorRef.current) editorRef.current.focus();
        },
        getEditor: () => editorRef.current,
        getMonacoContainer: () => monacoContainerRef.current,
    }));

    useEffect(() => {
        const findMonacoOverflowContainer = () => {
            if (!containerRef.current) return null;

            // Monaco creates divs with class names
            const overflowDiv = containerRef.current.querySelector('.monaco-editor .overflowingContentWidgets');
            if (overflowDiv) return overflowDiv.parentElement;

            // Alternative selector
            const editorDiv = containerRef.current.querySelector('.monaco-editor');
            return editorDiv;
        };

        // Wait for Monaco to render
        const timer = setTimeout(() => {
            const container = findMonacoOverflowContainer();
            if (container) {
                monacoContainerRef.current = container;
                // Call a callback to notify parent if needed
                if (props.onMonacoReady) {
                    props.onMonacoReady(container);
                }
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [props.onMonacoReady]);

    const handleEditorChange = (newValue = "") => {
        if (onChange) onChange(newValue);
    };

    const handleEditorDidMount = (editor, monaco) => {
        editorRef.current = editor;

        // Try to get Monaco's DOM container
        setTimeout(() => {
            const domNode = editor.getDomNode();
            if (domNode) {
                monacoContainerRef.current = domNode;
                if (props.onMonacoReady) {
                    props.onMonacoReady(domNode);
                }
            }
        }, 500);

        editor.addCommand(
            monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyV,
            async () => {
                console.log("Monaco paste command triggered");

                if (!onPaste) return;

                try {
                    const text = await navigator.clipboard.readText();
                    console.log("Clipboard text from Monaco:", text);

                    if (!text) return;

                    const model = editor.getModel();
                    const selection = editor.getSelection();

                    // 🔹 Convert Monaco (line/column) → absolute string offsets
                    const startOffset = model.getOffsetAt({
                        lineNumber: selection.startLineNumber,
                        column: selection.startColumn,
                    });
                    const endOffset = model.getOffsetAt({
                        lineNumber: selection.endLineNumber,
                        column: selection.endColumn,
                    });

                    const fakeEvent = {
                        preventDefault: () => { },
                        stopPropagation: () => { },
                        clipboardData: {
                            getData: () => text,
                        },
                        target: {
                            value: editor.getValue(),
                            selectionStart: startOffset,
                            selectionEnd: endOffset,
                        },
                    };

                    onPaste(fakeEvent); // -> useInterviewMetrics.handlePaste
                } catch (err) {
                    console.error("Failed to read clipboard in Monaco:", err);
                    // optional fallback:
                    editor.trigger("keyboard", "paste", null);
                }
            }
        );

        // keep keydown for metrics
        editor.onKeyDown((e) => {
            if (!onKeyDown) return;

            const key = e.browserEvent?.key || "";

            const syntheticEvent = {
                key,
                keyCode: e.browserEvent?.keyCode,
                code: e.browserEvent?.code,
                shiftKey: e.browserEvent?.shiftKey,
                ctrlKey: e.ctrlKey,
                altKey: e.altKey,
                metaKey: e.metaKey,
                preventDefault: () => e.preventDefault(),
                stopPropagation: () => e.stopPropagation(),
                target: {
                    value: editor.getValue(),
                },
            };

            onKeyDown(syntheticEvent);
        });
    };

    // Keep layout responsive
    useEffect(() => {
        const editor = editorRef.current;
        if (!editor) return;

        const handleResize = () => editor.layout();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const resolvedLanguage =
        typeof language === "string"
            ? ["js", "jsx"].includes(language)
                ? "javascript"
                : language === "ts"
                    ? "typescript"
                    : language === "py"
                        ? "python"
                        : language.toLowerCase()
            : "plaintext";

    return (
        <div
            ref={containerRef}
            style={{
                height,
                width: "100%",
                position: "relative",
                cursor: "text",
            }}
        >
            <Editor
                height={height}
                language={resolvedLanguage}
                value={value}
                onChange={handleEditorChange}
                onMount={handleEditorDidMount}
                editorDidMount={handleEditorDidMount}
                theme="vs-light"
                options={{
                    semanticHighlighting: true,
                    minimap: { enabled: false },
                    fontSize: 14,
                    fontFamily: "Fira Code, monospace",
                    wordWrap: "on",
                    scrollBeyondLastLine: false,
                    lineNumbers: "on",
                    automaticLayout: true,
                    formatOnPaste: false, // we control paste
                    formatOnType: true,
                    tabCompletion: "off",
                    suggestOnTriggerCharacters: false,
                    quickSuggestions: false,
                    contextmenu: true,
                    domReadOnly: false,
                    readOnly: false,
                }}
                {...props}
            />
        </div>
    );
};

export default forwardRef(CodeAnswerEditor);
